import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Sidebar } from './Sidebar';

/**
 * Layout untuk halaman yang butuh login.
 * Pola: Sidebar kiri + Topbar (brand di sidebar, user info + tombol logout di topbar) + content area.
 * Mengikuti pola Booku V2 (lihat .claude/docs/Booku v2 — frontend/src/components/layout).
 */
export function AppLayout({ children }: { children: ReactNode }) {
  const { klien, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleProfil = () => {
    navigate('/profil');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 bg-white border-b border-gray-200 px-6 flex items-center justify-end gap-4">
          {klien && (
            <div className="text-sm text-right hidden sm:block">
              <div className="font-medium text-gray-800 leading-tight">{klien.namaLengkap}</div>
              <div className="text-xs text-gray-500 leading-tight">@{klien.username}</div>
            </div>
          )}
          <button
            onClick={handleProfil}
            className="w-9 h-9 rounded-full border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-600"
            title="Profil"
          >
            <User size={18} />
          </button>
          <button
            onClick={handleLogout}
            className="w-9 h-9 rounded-full border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-600"
            title="Keluar"
          >
            <LogOut size={18} />
          </button>
        </header>

        <main className="flex-1 px-8 py-6 overflow-auto">
          <div className="max-w-5xl mx-auto">{children}</div>
        </main>
      </div>
    </div>
  );
}

/**
 * Layout untuk halaman publik (sign-up, login, verify). Centered card.
 */
export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">{children}</div>
    </div>
  );
}
